// Shared utilities for converting arbitrary supported files to PDF via
// ConvertAPI. These are pure functions (no hooks) so they can be used from
// both the classic usePdfToStorydoc hook and the new file-to-storydoc dialog
// orchestration hook.

export const CONVERT_API_BASE_URL = "https://v2.convertapi.com";

export const PDF_MIME_TYPE = "application/pdf";

// Extensions ConvertAPI can turn into a PDF for us. `pdf` is included so the
// passthrough branch is explicit.
export const SUPPORTED_EXTENSIONS = ["pdf", "docx", "doc", "pptx", "ppt", "txt"];

export const getFileExtension = (file: File): string => {
  return file.name.split(".").pop()?.toLowerCase() ?? "";
};

export const isPdfFile = (file: File): boolean => {
  return file.type === PDF_MIME_TYPE || getFileExtension(file) === "pdf";
};

export const isSupportedFile = (file: File): boolean => {
  return isPdfFile(file) || SUPPORTED_EXTENSIONS.includes(getFileExtension(file));
};

export const base64ToUint8Array = (base64: string): Uint8Array => {
  return Uint8Array.from(atob(base64), (m) => m.codePointAt(0)!);
};

/**
 * Converts a non-PDF file to PDF via ConvertAPI. PDFs pass through unchanged.
 * The token must be obtained externally (e.g. from useLazyGetConvertApiTokenQuery).
 */
export const convertFileToPdf = async (
  file: File,
  token: string,
  signal: AbortSignal,
): Promise<File> => {
  if (isPdfFile(file)) return file;

  const extension = getFileExtension(file);

  const formData = new FormData();
  formData.append("File", file);

  const response = await fetch(
    `${CONVERT_API_BASE_URL}/convert/${extension}/to/pdf?Token=${token}`,
    { method: "POST", body: formData, signal },
  );
  if (!response.ok) {
    throw new Error(
      `ConvertAPI request failed with status ${response.status}`,
    );
  }

  const { Files } = await response.json();
  const fileData = Files?.[0]?.FileData;
  if (!fileData) {
    throw new Error("ConvertAPI returned no converted file");
  }

  const baseName = file.name.replace(/\.[^.]+$/, "");
  return new File(
    [base64ToUint8Array(fileData) as unknown as BlobPart],
    `${baseName}.pdf`,
    { type: PDF_MIME_TYPE },
  );
};
